// Morphing between two series of numbers — what makes switching 1h → 24h, or
// Weekly → Monthly, read as the same chart changing rather than one chart being
// replaced by another.
//
// The hard part is that the two series rarely have the same LENGTH: 24 hourly
// samples become 40, seven days become thirty. So the old series is resampled
// onto the new one's positions first, and only then tweened point by point. CSS
// cannot do this — a `d` transition needs identical command counts — and a
// charting library would be a runtime dependency (§4.2), so it is ~40 lines of
// requestAnimationFrame here instead.
//
// Anyone who has asked their system not to animate gets the new values at once.

import { useEffect, useRef, useState } from 'react';

// Slow enough to watch the shape travel rather than just notice it changed —
// the point of the morph is that you can follow where a peak went.
const DURATION_MS = 1400;

/** Old values stretched onto `n` positions, so point i has something to be
 * tweened FROM even when the two windows have different resolutions. */
export function resample(src: number[], n: number): number[] {
  if (n <= 0) return [];
  if (src.length === 0) return new Array<number>(n).fill(0);
  if (src.length === n) return src.slice();
  if (src.length === 1) return new Array<number>(n).fill(src[0]!);
  return Array.from({ length: n }, (_, i) => {
    const p = (i / Math.max(n - 1, 1)) * (src.length - 1);
    const lo = Math.floor(p);
    const hi = Math.min(lo + 1, src.length - 1);
    return src[lo]! + (src[hi]! - src[lo]!) * (p - lo);
  });
}

/**
 * Ease IN: the change starts almost still and gathers pace, which is what makes
 * the morph readable — a decelerating curve spent 58% of the movement in the
 * first 200ms and read as instant however long the tween lasted.
 *
 * It arrives at full speed rather than settling, by design.
 */
const ease = (t: number) => t ** 3;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );
}

/**
 * Tween several series at once (they share one chart, so they must share one
 * clock — animating them separately would let the lines drift apart mid-flight).
 * Returns the values to draw this frame.
 */
export function useMorphedValues(targets: number[][]): number[][] {
  const [frame, setFrame] = useState<number[][]>(targets);
  // What is actually on screen right now — the start of the NEXT tween, so an
  // interrupted change carries on from where it visibly is, not from the last
  // completed value.
  const shown = useRef<number[][]>(targets);
  const raf = useRef<number | null>(null);
  const key = targets.map((t) => `${t.length}:${t.join(',')}`).join('|');

  useEffect(() => {
    if (raf.current !== null) cancelAnimationFrame(raf.current);

    const to = targets;
    // Reduced motion is the same code path with a zero-length tween: the first
    // frame lands on the real values. One path is easier to trust than two, and
    // it keeps the state update out of the effect body (cascading renders).
    const duration = prefersReducedMotion() ? 0 : DURATION_MS;
    const from = to.map((t, i) => resample(shown.current[i] ?? [], t.length));
    const started = performance.now();

    const step = (now: number) => {
      const t = duration === 0 ? 1 : Math.min(1, (now - started) / duration);
      const e = ease(t);
      const next = to.map((series, si) =>
        series.map((v, i) => {
          const a = from[si]?.[i] ?? v;
          return a + (v - a) * e;
        }),
      );
      shown.current = next;
      setFrame(next);
      if (t < 1) {
        raf.current = requestAnimationFrame(step);
      } else {
        shown.current = to; // land exactly on the real numbers, not on 0.999 of them
        setFrame(to);
        raf.current = null;
      }
    };
    raf.current = requestAnimationFrame(step);

    return () => {
      if (raf.current !== null) cancelAnimationFrame(raf.current);
      raf.current = null;
    };
    // `key` is the value-identity of the targets: a re-render with the same
    // numbers must not restart the animation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return frame;
}
